// =============================================================================
// FIELD PALETTE - Draggable fields list
// =============================================================================

import {
  Type,
  Mail,
  Hash,
  Phone,
  AlignLeft,
  ChevronDown,
  ListChecks,
  CheckSquare,
  CircleDot,
  Calendar,
  Clock,
  Upload,
  Image,
  PenTool,
  Star,
  LayoutTemplate,
  Minus,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { FIELD_TEMPLATES, type FieldTemplate, type FieldType } from '../types';

const ICON_MAP: Record<string, LucideIcon> = {
  Type,
  Mail,
  Hash,
  Phone,
  AlignLeft,
  ChevronDown,
  ListChecks,
  CheckSquare,
  CircleDot,
  Calendar,
  Clock,
  Upload,
  Image,
  PenTool,
  Star,
  LayoutTemplate,
  Minus,
};

interface DraggableFieldProps {
  template: FieldTemplate;
  onDragStart: (event: React.DragEvent, fieldType: FieldType) => void;
}

function DraggableField({ template, onDragStart }: DraggableFieldProps) {
  const Icon = ICON_MAP[template.icon] || Type;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, template.type)}
      className={cn(
        'flex items-center gap-2 rounded-lg border border-secondary-200 bg-white p-2 cursor-grab transition-all',
        'hover:border-primary-300 hover:shadow-sm',
        'dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-600',
        'active:cursor-grabbing'
      )}
    >
      <Icon className="h-4 w-4 text-secondary-500" />
      <span className="text-sm text-secondary-700 dark:text-secondary-300">
        {template.label}
      </span>
    </div>
  );
}

interface FieldPaletteProps {
  onDragStart: (event: React.DragEvent, fieldType: FieldType) => void;
  onAddField: (fieldType: FieldType) => void;
}

export function FieldPalette({ onDragStart, onAddField }: FieldPaletteProps) {
  const groupedTemplates = FIELD_TEMPLATES.reduce<Record<string, FieldTemplate[]>>(
    (acc, template) => {
      const category = acc[template.category];
      if (!category) {
        acc[template.category] = [template];
      } else {
        category.push(template);
      }
      return acc;
    },
    {}
  );

  const categoryLabels: Record<string, string> = {
    basic: 'Basic Fields',
    advanced: 'Advanced Fields',
    layout: 'Layout',
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-secondary-200 dark:border-secondary-700">
        <h3 className="font-semibold text-secondary-900 dark:text-white">Form Fields</h3>
        <p className="text-xs text-secondary-500 mt-1">
          Drag or click to add fields
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {Object.entries(groupedTemplates).map(([category, templates]) => (
          <div key={category}>
            <h4 className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-2">
              {categoryLabels[category]}
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {templates.map((template) => (
                <div
                  key={template.type}
                  onClick={() => onAddField(template.type)}
                >
                  <DraggableField
                    template={template}
                    onDragStart={onDragStart}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
